import type {
  GetAllProjectsNormalizedApiArg,
  GetFederationProjectsNormalizedApiArg,
  GetProjectNormalizedApiArg,
  GetSidebarProjectsNormalizedApiArg,
} from '../services/rest'
import type {ProjectId, RequestOptionsParams} from '../types'
import {ROOT_PROJECT_ID, stringifyId} from '../types'

import {buildTypeFields, linksAvailabilityField, linksField, parametersField} from './buildTypes'

const basicProjectFields = ['id', 'internalId', 'name', 'parentProjectId', 'archived', 'readOnlyUI']
const descriptionField = 'description'
const archivedProjectsIdField = 'projects(project(id),count,$locator(archived:true))'
const projectFields: (
  arg0: RequestOptionsParams | null | undefined,
) => ReadonlyArray<string> = options =>
  basicProjectFields
    .concat(
      options?.withBuildTypes === true
        ? `buildTypes(buildType(${buildTypeFields(options).join(',')}))`
        : [],
    )
    .concat(options?.withVirtual === true ? 'virtual' : [])
    .concat(options?.withHoldsPipeline === true ? 'holdsPipeline' : [])
    .concat(
      options?.withLinks === true
        ? linksField
        : options?.withLinksAvailability === true
          ? linksAvailabilityField
          : [],
    )
    .concat(options?.withParameters === true ? parametersField : [])
    .concat(options?.withDescription === true ? descriptionField : [])
    .concat(options?.withArchivedSubprojectsIds === true ? archivedProjectsIdField : [])
    .concat(
      options?.withPipelines === true
        ? 'pipelines(count,pipeline(id,internalId,name,headBuildType(id,projectId),parentProject(id)))'
        : [],
    )
    .concat(
      options?.withAncestorProjects === true
        ? `ancestorProjects(${options?.withOnlyArchivedAncestorProjects ? '$locator(archived:true),' : ''}project(${projectFields({withLinksAvailability: options?.withLinksAvailability}).join(',')}))`
        : [],
    )
export const getProjectsArg = (
  projectId: ProjectId,
  options?: RequestOptionsParams,
): GetAllProjectsNormalizedApiArg => {
  const childrenLocator = `archived:${options?.archived ? options.archived : 'false'},${
    options?.withLowNesting === true ? 'project' : 'affectedProject'
  }:${stringifyId(projectId)}`
  return {
    ...options,
    locator:
      options?.includeRoot === true
        ? `item(id:${stringifyId(projectId)}),item(${childrenLocator})`
        : childrenLocator,
    fields: `project(${projectFields({...options, withPipelines: true}).join(',')})`,
    projectId,
  }
}

export const getProjectPageArg = (projectId: ProjectId) =>
  getProjectsArg(projectId, {
    withBuildTypes: true,
    withAncestorProjects: true,
    withLinksAvailability: true,
  })

export const archivedProjectsArg: GetSidebarProjectsNormalizedApiArg = getProjectsArg(
  ROOT_PROJECT_ID,
  {
    isSidebar: true,
    withBuildTypes: true,
    withLinksAvailability: true,
    archived: 'any',
  },
)

export const sidebarArg: GetSidebarProjectsNormalizedApiArg = {
  isSidebar: true,
  withBuildTypes: true,
  withAncestorProjects: true,
  withOnlyArchivedAncestorProjects: true,
  withLinksAvailability: true,
  customEndpoint: '/sidebar/projects/active',
}

export const overviewArg: GetSidebarProjectsNormalizedApiArg = {
  isOverview: true,
  isSidebar: true,
  withAncestorProjects: true,
  customEndpoint: '/sidebar/projects/favorite',
}

export const agentsProjectsArg = getProjectsArg(ROOT_PROJECT_ID, {
  withBuildTypes: false,
  withLinks: true,
})

export const getFederationProjectsArg = (
  federationServerUrl: string,
  withArchived: boolean,
): GetFederationProjectsNormalizedApiArg => ({
  ...getProjectsArg(ROOT_PROJECT_ID, {
    withBuildTypes: true,
    archived: withArchived ? 'any' : 'false',
  }),
  isSidebar: true,
  federationServerUrl,
})

export const getSingleProjectArg = (
  projectId: ProjectId,
  options: RequestOptionsParams = {
    withBuildTypes: true,
    withLinks: true,
    withParameters: true,
    withArchivedSubprojectsIds: true,
    withDescription: true,
    withPauseComment: true,
  },
): GetProjectNormalizedApiArg => ({
  ...options,
  projectLocator: `id:${stringifyId(projectId)}`,
  fields: projectFields({
    ...options,
    withPipelines: true,
    withVirtual: true,
    withHoldsPipeline: true,
  }).join(','),
  essential: true,
})
